/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Pressable, View } from 'react-native';
import { getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import { pd } from '@airasia-phoenix/react-native/tokens';
import { IconViewProps } from '@airasia-phoenix/react-native/components/IconView/types';
import IconView from '@airasia-phoenix/react-native/components/IconView/index';
import ClickableIcon from './ClickableIcon';
import AvatarView, {
  AvatarState,
  AvatarViewSize,
} from '@airasia-phoenix/react-native/components/AvatarView/index';
import SkeletonLoaderView from '@airasia-phoenix/react-native/components/SkeletonLoaderView';
import DividerView from '@airasia-phoenix/react-native/components/DividerView/index';
import {
  DividerDirection,
  DividerType,
  DividerViewProps,
} from '@airasia-phoenix/react-native/components/DividerView/types';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { useUnistyles } from 'react-native-unistyles';
import { styles } from './styles';
import { NavigationBar } from './NavigationBar';
import { NavigationItem } from './types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useContainerWidth, useDeviceInfo, useAppConfig } from '@airasia-rn/ui';
import AccountCircle from '@move-travel/react-native-phoenix-icons/AccountCircle';
import LightMode from '@move-travel/react-native-phoenix-icons/LightMode';
import DarkMode from '@move-travel/react-native-phoenix-icons/DarkMode';
import { AuthWidgetVersionToggle } from './AuthWidgetVersionToggle';
import LocaleCurrencySelector from './LocaleCurrencySelector';
import CookieBanner from '../../components/CookieBanner';
import type { CookieDisclaimer } from '../../services/types';

export interface WebHeaderViewProps {
  height?: number;
  content?: React.ReactNode;
  navigationItems?: NavigationItem[];
  currencyText?: string;
  languageText?: string;
  onCurrencyPress?: () => void;
  onLanguagePress?: () => void;
  moveLogoUrl?: string;
  moveRedirectUrl?: string;
  isLoggedIn?: boolean;
  isUserDetailLoading?: boolean;
  accountAvatarUrl?: string;
  isAccountAvatarLoading?: boolean;
  accountText?: string;
  accountAvatarText?: string;
  onAccountPress?: () => void;
  accountButtonRef?: React.Ref<React.ComponentRef<typeof Pressable>>;
  onLogoPress?: () => void;
  onNavigationItemPress?: (item: NavigationItem) => void;
  endIcon?: IconViewProps;
  onEndIconPress?: () => void;
  onDarkModeToggle?: () => void;
  darkModeToggleEnabled?: boolean;
  bottomDividerView?: DividerViewProps;
  testID?: string;
  cookieDisclaimer?: CookieDisclaimer;
  /** Cookie-banner consent cookie value resolved on the server, used for correct SSR rendering of the cookie banner. */
  serverCookieValueForCookieBanner?: string | null;
  /** Opt in to the hydration-safe cookie banner. See CookieBannerProps.hydrationSafe. */
  hydrationSafeCookieBanner?: boolean;
  loginTooltip?: React.ReactNode;
  /** Optional extra control rendered at the start of the mobile header's right-side controls (e.g. a search icon). */
  mobileEndSlot?: React.ReactNode;
  /** Use a narrower desktop gutter (3% instead of 8.5% at superLarge). Opt-in per LOB. */
  compactHorizontalPadding?: boolean;
}

const WebHeaderView: React.FC<WebHeaderViewProps> = ({
  height,
  content,
  navigationItems = [],
  moveLogoUrl,
  moveRedirectUrl,
  currencyText,
  languageText,
  onCurrencyPress,
  onLanguagePress,
  isLoggedIn,
  isUserDetailLoading = false,
  accountText,
  accountAvatarText = accountText,
  accountAvatarUrl,
  isAccountAvatarLoading = false,
  onAccountPress,
  accountButtonRef,
  onLogoPress,
  onNavigationItemPress,
  endIcon,
  onEndIconPress,
  onDarkModeToggle,
  darkModeToggleEnabled,
  bottomDividerView,
  cookieDisclaimer,
  serverCookieValueForCookieBanner,
  hydrationSafeCookieBanner,
  loginTooltip,
  mobileEndSlot,
  compactHorizontalPadding = false,
}) => {
  const { theme } = useUnistyles();
  const { t } = useTranslation('commonWeb');
  const { containerWidth, onLayout } = useContainerWidth();
  const { isMobileWidth } = useDeviceInfo(containerWidth || undefined);
  const { toggleDarkMode } = useAppConfig();
  const accountAvatar = accountAvatarUrl
    ? { url: accountAvatarUrl, testID: 'web-header-account-avatar-image' }
    : undefined;
  const loggedOutAccountIcon = {
    height: 24,
    width: 24,
    tint: pd.sys.colour.content_primary.toString(),
    children: <AccountCircle />,
  };

  // A header showing the MOVE logo should always be clickable. Locale-agnostic
  // on purpose — airasia.com redirects to the visitor's locale itself. LOBs that
  // pass moveRedirectUrl (e.g. a locale-specific or in-app target) still win.
  const redirectUrl = moveRedirectUrl ?? 'https://www.airasia.com';
  return (
    <View onLayout={onLayout}>
      <CookieBanner
        cookieDisclaimer={cookieDisclaimer}
        serverCookieValueForCookieBanner={serverCookieValueForCookieBanner}
        hydrationSafe={hydrationSafeCookieBanner}
      />
      {isMobileWidth && (
        <View style={[styles.containerMobile, { height: height ?? 48 }]}>
          {accountText && (
            <Pressable
              ref={accountButtonRef}
              onPress={onAccountPress}
              style={styles.mobileAccount}
            >
              <View pointerEvents="none">
                <AvatarView
                  size={AvatarViewSize.XS}
                  text={
                    isLoggedIn && !accountAvatar ? accountAvatarText : undefined
                  }
                  backgroundColor={
                    isLoggedIn ? undefined : pd.sys.colour.bg_primary.toString()
                  }
                  icon={isLoggedIn ? accountAvatar : loggedOutAccountIcon}
                  state={
                    isUserDetailLoading || isAccountAvatarLoading
                      ? AvatarState.LOADING
                      : AvatarState.LOADED
                  }
                />
              </View>
              {isUserDetailLoading ? (
                <SkeletonLoaderView style={styles.mobileAccountSkeleton} />
              ) : (
                accountText &&
                isLoggedIn && (
                  <TextView
                    spannableParentTextStyle={styles.accountTextParent}
                    text={accountText}
                    numberOfLines={1}
                    style={[
                      getTextStyle(
                        pd.sys.typescale.sub_body_text,
                        pd.sys.colour.content_primary,
                      ),
                      styles.mobileAccountText,
                    ]}
                  />
                )
              )}
            </Pressable>
          )}

          <View style={styles.mobileMoveLogo} pointerEvents="box-none">
            <ClickableIcon
              height={22}
              width={100}
              redirectUrl={redirectUrl}
              logoIcon={moveLogoUrl ? { url: moveLogoUrl } : undefined}
              onPress={onLogoPress}
            />
          </View>

          <View style={styles.mobileRight}>
            {mobileEndSlot}
            <AuthWidgetVersionToggle
              isMobile
              darkModeToggleEnabled={darkModeToggleEnabled}
            />

            <LocaleCurrencySelector
              isMobile
              currencyText={currencyText}
              languageText={languageText}
              onCurrencyPress={onCurrencyPress}
              onLanguagePress={onLanguagePress}
            />

            {darkModeToggleEnabled && (
              <Pressable
                onPress={onDarkModeToggle ?? toggleDarkMode}
                style={styles.mobileDarkMode}
              >
                <IconView
                  height={24}
                  width={24}
                  tint={pd.sys.colour.content_primary.hex}
                  style={styles.localeCurrencyIcon}
                >
                  {theme.isDarkMode ? <LightMode /> : <DarkMode />}
                </IconView>
              </Pressable>
            )}
          </View>
          {loginTooltip}
        </View>
      )}
      {!isMobileWidth && (
        <View
          style={[
            styles.container,
            compactHorizontalPadding ? styles.containerCompactPadding : null,
            { height: height ?? 64 },
          ]}
        >
          <View style={styles.moveLogo}>
            <ClickableIcon
              height={33}
              width={styles.moveLogo.width}
              redirectUrl={redirectUrl}
              logoIcon={moveLogoUrl ? { url: moveLogoUrl } : undefined}
              onPress={onLogoPress}
            />
          </View>
          <View style={styles.content}>
            {content ?? (
              <NavigationBar
                navigationItems={navigationItems}
                onItemPress={onNavigationItemPress}
              />
            )}
          </View>
          <View style={styles.endSection}>
            {(currencyText || languageText) && (
              <>
                {navigationItems.length > 0 && (
                  <View style={styles.dividerWrapper}>
                    <DividerView
                      type={DividerType.Solid}
                      direction={DividerDirection.Vertical}
                      thickness={1}
                      height={32}
                    />
                  </View>
                )}
                <LocaleCurrencySelector
                  currencyText={currencyText}
                  languageText={languageText}
                  onCurrencyPress={onCurrencyPress}
                  onLanguagePress={onLanguagePress}
                />
              </>
            )}

            {accountText && (
              <>
                <View style={styles.dividerWrapper}>
                  <DividerView
                    type={DividerType.Solid}
                    direction={DividerDirection.Vertical}
                    thickness={1}
                    height={32}
                  />
                </View>
                <View style={styles.accountWrapper}>
                  <Pressable
                    ref={accountButtonRef}
                    onPress={onAccountPress}
                    style={styles.account}
                  >
                    <View pointerEvents="none">
                      <AvatarView
                        size={AvatarViewSize.XS}
                        text={
                          isLoggedIn && !accountAvatar
                            ? accountAvatarText
                            : undefined
                        }
                        backgroundColor={
                          isLoggedIn
                            ? undefined
                            : pd.sys.colour.bg_primary.toString()
                        }
                        icon={isLoggedIn ? accountAvatar : loggedOutAccountIcon}
                        state={
                          isUserDetailLoading || isAccountAvatarLoading
                            ? AvatarState.LOADING
                            : AvatarState.LOADED
                        }
                      />
                    </View>
                    {isUserDetailLoading ? (
                      <SkeletonLoaderView style={styles.accountSkeleton} />
                    ) : (
                      <TextView
                        spannableParentTextStyle={
                          !isLoggedIn ? {} : styles.accountTextParent
                        }
                        text={accountText}
                        style={[
                          getTextStyle(
                            pd.sys.typescale.sub_body_text,
                            pd.sys.colour.content_primary,
                          ),
                          styles.accountLabel,
                        ]}
                      />
                    )}
                  </Pressable>
                  {loginTooltip}
                </View>
              </>
            )}

            <AuthWidgetVersionToggle />

            {endIcon && (
              <Pressable
                onPress={onEndIconPress}
                accessibilityRole="button"
                accessibilityLabel={t(
                  'webHeaderView.accessibility.openMoreOptions',
                )}
              >
                <IconView
                  height={24}
                  width={24}
                  tint={pd.sys.colour.content_primary.hex}
                  {...endIcon}
                />
              </Pressable>
            )}

            {darkModeToggleEnabled && (
              <>
                <View style={styles.dividerWrapper}>
                  <DividerView
                    type={DividerType.Solid}
                    direction={DividerDirection.Vertical}
                    thickness={1}
                    height={32}
                  />
                </View>

                <Pressable onPress={onDarkModeToggle ?? toggleDarkMode}>
                  <IconView
                    height={24}
                    width={24}
                    tint={pd.sys.colour.content_primary.hex}
                    style={styles.localeCurrencyIcon}
                  >
                    {theme.isDarkMode ? <LightMode /> : <DarkMode />}
                  </IconView>
                </Pressable>
              </>
            )}
          </View>
        </View>
      )}
      {bottomDividerView && (
        <DividerView width={'100%'} {...bottomDividerView} />
      )}
    </View>
  );
};

export default WebHeaderView;
